import Logo from "@funded-here-interface/common/src/assets/logo2.png";
import { performLogoutCleanup } from "@funded-here-interface/common/src/services/standardizedLogout";
import { Avatar, Group, NavLink, ScrollArea, Text } from "@mantine/core";
import { Icon, IconLogout } from "@tabler/icons-react";
import { FC, useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import useLogoutMutation from "../../hooks/useLogout";
import { RootState, store, persistor } from "../../store";
import { adminResetActions } from "../../Utils/adminResetActions";
import { useStyles } from "./AdminNavBar.styles";

export interface IMenuItem {
  label: string;
  path?: string;
  /**
   * Also highlight this leaf for any page under this path segment, e.g.
   * "/pm-dashboard" keeps "Dashboard" active on every /pm-dashboard/* tab
   * while `path` stays the tab it lands on.
   */
  activePrefix?: string;
  submenu?: ISubMenu[];
  icon: Icon;
}

interface ISubMenu {
  label: string;
  path: string;
}

interface INavBar {
  menus: IMenuItem[];
  userRole?: string;
}

const AdminNavBar: FC<INavBar> = ({ menus, userRole }) => {
  const { classes } = useStyles();
  const queryClient = useQueryClient();

  const [activeSubMenu, setActiveSubMenu] = useState<number | null>(null);
  const navigation = useNavigate();
  const { token, userId } = useSelector((state: RootState) => state.auth);
  const location = useLocation();
  const logoutMutation = useLogoutMutation();

  // Automatically open submenu if any submenu path matches the current location.pathname
  useEffect(() => {
    menus.forEach((menu, index) => {
      if (menu.submenu && isCurrentPage(getAllSubPaths(menu.submenu))) {
        setActiveSubMenu(index);
      }
    });
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserves existing hook behavior; verify in follow-up audit
  }, [location.pathname]); // This runs when the location.pathname changes

  const handleLogout = () => {
    logoutMutation.mutate(
      { token, userId },
      {
        onSuccess: () => {
          performLogoutCleanup({
            navigate: navigation,
            store,
            persistor,
            queryClient,
            resetActions: adminResetActions,
          });
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      },
    );
  };

  const toggleSubMenu = (index: any) => {
    setActiveSubMenu(activeSubMenu === index ? null : index);
  };

  const isCurrentPage = (paths: string[]) => {
    return paths.includes(location.pathname);
  };

  const getAllSubPaths = (subMenu: ISubMenu[]): string[] => {
    return subMenu.map((item) => item.path);
  };

  // Trailing "/" so "/pm-dashboard" doesn't also claim "/pm-dashboard-x".
  const isLeafActive = (item: IMenuItem) =>
    item.path === location.pathname ||
    (!!item.activePrefix && location.pathname.startsWith(`${item.activePrefix}/`));

  const items = menus.map((item, index) => {
    if (item.submenu) {
      // If there are submenus, render nested NavLink
      return (
        <NavLink
          key={index}
          label={item.label}
          className={classes.link}
          childrenOffset={20}
          leftSection={<item.icon size="1.5rem" stroke={1.5} />}
          opened={activeSubMenu === index} // Control submenu open state
          onClick={() => toggleSubMenu(index)} // Toggle open/closed state
        >
          {item.submenu.map((submenu, subIndex) => (
            <NavLink
              key={subIndex}
              label={submenu.label}
              component={Link}
              to={submenu.path}
              className={classes.link}
              active={submenu.path === location.pathname}
            />
          ))}
        </NavLink>
      );
    }

    // Render top-level menu item — a leaf (no submenu) must have a path
    if (!item.path) return null;

    return (
      <NavLink
        key={index}
        label={item.label}
        component={Link}
        to={item.path}
        className={classes.link}
        leftSection={<item.icon size="1.5rem" stroke={1.5} />}
        active={isLeafActive(item)}
      />
    );
  });

  return (
    <nav className={classes.sideBarContainer}>
      <div className={classes.iconbox}>
        <img className={classes.logo} alt="LOGO" src={Logo} />
      </div>
      <ScrollArea className={classes.scrollArea}>
        <div>{items}</div>
      </ScrollArea>

      <div className={classes.footer}>
        {userRole && (
          <Group my={10}>
            <Avatar radius="xl" />
            <div style={{ flex: 1 }}>
              <Text c="white" size="sm">
                {userRole}
              </Text>
            </div>
          </Group>
        )}

        <NavLink
          label={"Logout"}
          leftSection={<IconLogout size="1.5rem" stroke={1.5} />}
          onClick={handleLogout}
          className={classes.link}
        />
      </div>
    </nav>
  );
};

export default AdminNavBar;
